import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { usePermissions } from "@/lib/permissions";
import { integrationService, bouncieService } from "@/services/api";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { SkeletonList } from "@/components/common/Skeletons";
import { dateTime, relativeTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Cpu, Map, MessageSquare, Mail, Building2, Check, Plug, Activity, RefreshCw, Unplug, AlertTriangle } from "lucide-react";

const LOGOS = {
  Bouncie: Cpu, "Google Maps": Map, Mapbox: Map, "SMS Gateway": MessageSquare, Email: Mail, "CRM Sync": Building2,
};

export default function Integrations() {
  const { data: integrations, loading } = useFetch(() => integrationService.list(), []);
  const [params, setParams] = useSearchParams();
  const bouncieResult = params.get("bouncie");

  useEffect(() => {
    if (!bouncieResult) return;
    if (bouncieResult === "connected") toast.success("Bouncie connected");
    else toast.error(`Could not connect Bouncie (${params.get("reason") || "unknown error"})`);
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bouncieResult]);

  const connected = (integrations || []).filter((i) => i.status === "connected" && i.name !== "Bouncie");
  const available = (integrations || []).filter((i) => i.status === "available");

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader title="Integrations" subtitle="Connect ARC Beacon to telemetry providers, mapping and notification services." />

      <div className="space-y-8">
        <Section title="Vehicle Data">
          <BouncieCard />
        </Section>

        {loading ? <SkeletonList rows={4} /> : (
          <>
            <Section title="Connected">
              {/* auto-fit + minmax: one connected integration fills the row
                  instead of leaving two empty grid tracks beside it, and the
                  grid still wraps to more columns as more get connected. */}
              <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-3">
                {connected.map((i) => <IntegrationCard key={i.id} i={i} />)}
                {connected.length === 0 && <p className="text-sm text-muted-foreground">No other active integrations.</p>}
              </div>
            </Section>

            <Section title="Available in future">
              <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-3">
                {available.map((i) => <IntegrationCard key={i.id} i={i} />)}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function BouncieCard() {
  const { isManager } = usePermissions();
  const { data: status, loading, refetch } = useFetch(() => bouncieService.status(), []);
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    setBusy(true);
    try {
      await bouncieService.connect(); // navigates away on success
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not start Bouncie connect flow");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await bouncieService.disconnect();
      toast.success("Bouncie disconnected");
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not disconnect Bouncie");
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      await bouncieService.syncNow();
      toast.success("Sync queued — new data will appear shortly.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not queue a sync");
    } finally {
      setBusy(false);
    }
  };

  const connected = status?.connected;
  // "Not connected" and "was connected, now refused" need different words and
  // different urgency: the second one means numbers already on screen have
  // stopped updating, which is not something a neutral grey chip conveys.
  const needsReauth = Boolean(status?.needsReauth ?? status?.needs_reauth);

  return (
    <Card className="relative overflow-hidden border-border/70 bg-card/60 p-6" data-testid="integration-bouncie">
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-emerald-400/5 blur-2xl" />
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border/60 bg-secondary/50"><Cpu className="h-7 w-7" /></div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-xl font-semibold">Bouncie</h3>
              {loading ? null : connected ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300"><Check className="h-3 w-3" /> Connected</span>
              ) : needsReauth ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/25 bg-amber-400/10 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300"><AlertTriangle className="h-3 w-3" /> Reconnect needed</span>
              ) : (
                <span className="rounded-full border border-border/60 px-2.5 py-0.5 text-xs font-medium text-muted-foreground">Not connected</span>
              )}
            </div>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">Connected vehicle telemetry via Bouncie OBD-II devices — real-time trips, location and alerts.</p>
          </div>
        </div>
        {needsReauth && (
          <div className="max-w-md rounded-lg border border-amber-400/20 bg-amber-400/[0.06] p-3 text-xs">
            <p className="font-medium text-amber-200">Bouncie stopped accepting our authorization.</p>
            <p className="mt-1 text-amber-200/70">
              Trips and geofence crossings still arrive by webhook, but anything read
              back from Bouncie — odometer, fuel, battery, trip reconciliation — has
              not updated
              {status.last_success_at ? ` since ${dateTime(status.last_success_at)}` : ""}.
              Reconnecting restores it; nothing already recorded is lost.
            </p>
            {status.last_error && (
              <p className="mt-1.5 font-mono text-[11px] text-amber-200/50">{status.last_error}</p>
            )}
          </div>
        )}
        {connected && status.expires_at && (
          <div className="grid grid-cols-1 gap-6">
            <div><div className="flex items-center gap-1.5 font-display text-sm font-medium text-emerald-600 dark:text-emerald-400"><Activity className="h-4 w-4" /> Token valid</div><div className="mt-1 text-xs text-muted-foreground">Renews {relativeTime(status.expires_at)}</div></div>
          </div>
        )}
      </div>
      {isManager && (
        <div className="mt-5 flex gap-2 border-t border-border/60 pt-5">
          {connected ? (
            <>
              <Button variant="secondary" size="sm" disabled={busy} onClick={syncNow}><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Sync now</Button>
              <Button variant="ghost" size="sm" disabled={busy} onClick={disconnect} className="text-red-400 hover:text-red-400"><Unplug className="mr-1.5 h-3.5 w-3.5" /> Disconnect</Button>
            </>
          ) : (
            <Button size="sm" disabled={busy || loading} onClick={connect} data-testid="connect-bouncie">{needsReauth ? "Reconnect Bouncie" : "Connect Bouncie"}</Button>
          )}
        </div>
      )}
    </Card>
  );
}

function IntegrationCard({ i }) {
  const Icon = LOGOS[i.name] || Plug;
  const connected = i.status === "connected";
  return (
    <Card className="border-border/70 bg-card/50 p-5" data-testid={`integration-${i.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border/60 bg-secondary/40"><Icon className="h-5 w-5" /></div>
        {connected ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300"><Check className="h-3 w-3" /> Connected</span>
        ) : (
          <span className="rounded-full border border-border/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">Available in future</span>
        )}
      </div>
      <h3 className="mt-3 font-medium">{i.name}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{i.description}</p>
      <Button variant={connected ? "ghost" : "secondary"} size="sm" className="mt-4 w-full" onClick={() => toast.info(connected ? `Manage ${i.name} — not built yet` : `Connect ${i.name} — not built yet`)} data-testid={`integration-action-${i.id}`}>
        {connected ? "Configure" : "Connect"}
      </Button>
    </Card>
  );
}
